import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withKeys as _withKeys, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = ["data-id"]
const _hoisted_2 = { class: "flex justify-between gap-2" }
const _hoisted_3 = { class: "text-gray-80 text-base font-medium" }
const _hoisted_4 = {
  key: 0,
  class: "text-gray-60 text-small font-small mt-2"
}
const _hoisted_5 = {
  key: 1,
  class: "text-gray-50"
}
const _hoisted_6 = {
  key: 1,
  class: "text-gray-70 hover:text-gray-90 group mt-5 flex w-full items-center outline-0"
}
const _hoisted_7 = {
  key: 0,
  class: "bg-gray-30 -mx-4 my-2 h-[1px] flex-1"
}
const _hoisted_8 = ["src"]
const _hoisted_9 = {
  key: 2,
  class: "w-full truncate"
}
const _hoisted_10 = ["src"]
const _hoisted_11 = {
  key: 2,
  class: "text-gray-50"
}

import { UsersIcon } from '@klausapp/ui-kit/icons'
import {
  UiButton,
  UiDropdownTypes,
  UiDropdownV2 as UiDropdown,
  UiInput,
  UiFormField,
  UiMenuSelectTrigger,
  UiMenuSimpleTrigger,
  UiTag,
  GlobalEvents,
} from '@klausapp/ui-kit'
import { computed, ref, watch } from 'vue'
import { cloneDeep } from 'lodash-es'
import { onClickOutside } from '@vueuse/core'

import { Tippy } from '@/components/Tippy'
import DayPicker from '@/components/pickers/DayPicker.vue'
import TagPicker from '@/components/pickers/TagPicker.vue'
import i18n from '@/i18n'

import { FilterItemStatus } from '@/modules/conversations/api/filters'
import {
  FilterValueType,
  type Condition,
  type ValueComponentProps,
  type FilterItemValue,
  type DropdownSelectedValue,
  type FilterItemEmit,
  GROUP,
} from './types'
import { isActiveFilter, valuesFilled } from './utils'
import CustomFieldValueDropdown from './CustomFieldValueDropdown.vue'
import MultirowTextInput from './MultirowTextInput.vue'
import FilterTrigger from './FilterTrigger.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'FilterItem',
  props: {
    filterId: {},
    filterLabel: {},
    description: {},
    conditions: {},
    selectedCondition: {},
    value: {},
    valueType: {},
    valueComponentProps: {},
    intelligenceIcon: { type: Boolean },
    botIcon: { type: Boolean },
    forceShowRemoveButton: { type: Boolean },
    forceHideRemoveButton: { type: Boolean },
    showAddSimilarButton: { type: Boolean },
    disabled: { type: Boolean },
    error: { type: Boolean },
    skipErrorCheck: { type: Boolean },
    suffix: {},
    status: {},
    isSpotlight: { type: Boolean }
  },
  emits: ["remove", "filter-applied", "add-similar-filter"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const { Modes, SelectAllModes } = UiDropdownTypes

const tippyRef = ref<InstanceType<typeof Tippy>>()
const filterItemPopup = ref<HTMLDivElement>()
const newSelectedCondition = ref<Condition>()
const newValue = ref<FilterItemValue>()
const filterApplied = ref(false)
const addingSimilarFilter = ref(false)

const tippyShown = computed(() => tippyRef.value?.tip.state.isVisible)

watch(
  () => [props.value, addingSimilarFilter.value],
  () => (newValue.value = addingSimilarFilter.value ? [] : cloneDeep(props.value)),
  { immediate: true },
)

watch(
  () => [props.selectedCondition, props.conditions, addingSimilarFilter.value],
  () => {
    if (!Object.keys(props.selectedCondition).length || addingSimilarFilter.value) {
      newSelectedCondition.value = props.conditions?.[0]
    } else newSelectedCondition.value = props.selectedCondition
  },
  { immediate: true },
)

const activeFilter = computed(() => isActiveFilter(props.value, props.selectedCondition))

// Count only the original groups from props, excluding dynamically added groups (like "All" or selected items)
// This ensures select-all remains visible even when items are selected and sorted to top
const hasMultipleGroups = computed(() => {
  const originalGroups = props.valueComponentProps?.groups || []
  // Filter out any auto-generated groups (they typically have special IDs or have `name: undefined` or `name: null`).
  // Note: Some original groups can have `name: ''` (empty string), so we use != null which excludes both null and undefined, but includes empty strings.
  const userDefinedGroups = originalGroups.filter((group) => group.name != null)
  return userDefinedGroups.length > 1
})

const applyingDisabled = computed(() => {
  if (props.skipErrorCheck) return false

  return newSelectedCondition.value?.valueRequired ? !valuesFilled(newValue.value) : !newSelectedCondition.value.label
})

const multipleValuesAllowed = computed(() =>
  Boolean(
    newSelectedCondition.value?.name?.startsWith('LONG_LIST') ||
      newSelectedCondition.value?.name?.startsWith('STRING_LIST') ||
      newSelectedCondition.value?.name?.includes('BREACHED'),
  ),
)

const getEmojiSrc = (value: DropdownSelectedValue[]) => {
  if (props.valueType !== FilterValueType.Generic) return
  if (value.length !== 1) return

  const selectedOption = (props.valueComponentProps?.groups ?? [])
    .map((g) => g.options)
    .flat()
    .find((o) => o.value === value[0].value)
  return selectedOption?.emojiSrc
}

const emojiSrc = computed(() => getEmojiSrc(props.value as DropdownSelectedValue[]))

const popoverHide = () => {
  addingSimilarFilter.value = false

  if (!filterApplied.value && !activeFilter.value) emit('remove')
  filterApplied.value = false
}

const hidePopover = () => {
  tippyRef.value?.tip.hide()
}

const applyClicked = ref(false)

const applyFilter = () => {
  applyClicked.value = true
  if (applyingDisabled.value) return

  if (addingSimilarFilter.value) {
    emit('add-similar-filter', {
      selectedCondition: newSelectedCondition.value,
      value: newValue.value,
    })
    addingSimilarFilter.value = false
  } else {
    emit('filter-applied', {
      selectedCondition: newSelectedCondition.value,
      value: newValue.value,
    })
  }

  filterApplied.value = true
  tippyRef.value?.tip.hide()
}

const getFieldLabel = (values: DropdownSelectedValue[]) => {
  if (!values.length) return null
  if (values.length === 1) return values[0].label

  return i18n.t('universal.items_selected', values.length)
}

const validateNumber = (value: number) => {
  if (value < 0) return (newValue.value = '')
  newValue.value = `${value}`
}

onClickOutside(filterItemPopup, (e: Event) => {
  if (e.target instanceof Element && !e.target.closest('[data-tippy-root]')) hidePopover()
})

return (_ctx: any,_cache: any) => {
  const _component_i18n_t = _resolveComponent("i18n-t")!
  const _directive_tippy = _resolveDirective("tippy")!

  return (_openBlock(), _createBlock(_unref(Tippy), {
    ref_key: "tippyRef",
    ref: tippyRef,
    trigger: props.disabled ? 'manual' : 'click',
    theme: "light paddingless",
    placement: "bottom-start",
    arrow: false,
    interactive: "",
    "hide-on-click": false,
    onKeydown: _withKeys(hidePopover, ["esc"]),
    onHide: popoverHide
  }, {
    trigger: _withCtx(() => [
      _createVNode(_unref(GlobalEvents), {
        onKeydown: _withKeys(hidePopover, ["esc"])
      }),
      _createVNode(FilterTrigger, {
        "data-testid": `filter-item-${_ctx.filterId}`,
        "tippy-shown": tippyShown.value,
        description: _ctx.description,
        "active-filter": activeFilter.value,
        disabled: _ctx.disabled,
        "intelligence-icon": _ctx.intelligenceIcon,
        "bot-icon": _ctx.botIcon,
        "filter-label": _ctx.filterLabel,
        "selected-condition": _ctx.selectedCondition,
        "emoji-src": emojiSrc.value,
        value: _ctx.value,
        "value-type": _ctx.valueType,
        "force-show-remove-button": _ctx.forceShowRemoveButton,
        "force-hide-remove-button": _ctx.forceHideRemoveButton,
        "show-add-similar-button": _ctx.showAddSimilarButton,
        error: _ctx.error,
        suffix: _ctx.suffix,
        onAdd: _cache[0] || (_cache[0] = ($event: any) => (addingSimilarFilter.value = true)),
        onRemove: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('remove')))
      }, null, 8 /* PROPS */, ["data-testid", "tippy-shown", "description", "active-filter", "disabled", "intelligence-icon", "bot-icon", "filter-label", "selected-condition", "emoji-src", "value", "value-type", "force-show-remove-button", "force-hide-remove-button", "show-add-similar-button", "error", "suffix"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", {
        ref_key: "filterItemPopup",
        ref: filterItemPopup,
        class: "min-w-[320px] p-4",
        "data-id": `filter-item-popup-${_ctx.filterLabel.toLocaleLowerCase().replace(' ', '_')}`
      }, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, _toDisplayString(_ctx.filterLabel), 1 /* TEXT */),
          (_ctx.status === _unref(FilterItemStatus).INACTIVE)
            ? _withDirectives((_openBlock(), _createBlock(_unref(UiTag), {
                key: 0,
                class: "mt-1"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('filter.tag.inactive.label')), 1 /* TEXT */)
                ]),
                _: 1 /* STABLE */
              })), [
                [_directive_tippy, {
            content: _ctx.isSpotlight ? _ctx.$t('filter.tag.inactive_spotlight.tooltip') : _ctx.$t('filter.tag.inactive.tooltip'),
          }]
              ])
            : _createCommentVNode("v-if", true)
        ]),
        _createVNode(_component_i18n_t, {
          keypath: "filter.option.concatenated",
          tag: "div"
        }, {
          description: _withCtx(() => [
            (_ctx.description)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_ctx.description), 1 /* TEXT */))
              : _createCommentVNode("v-if", true)
          ]),
          condition: _withCtx(() => [
            (_ctx.conditions?.length > 1)
              ? (_openBlock(), _createBlock(_unref(UiDropdown), {
                  key: 0,
                  mode: _unref(Modes).Single,
                  "model-value": newSelectedCondition.value ? [newSelectedCondition.value] : [],
                  "close-on-select": "",
                  "max-width": 999,
                  options: _ctx.conditions,
                  "search-placeholder": _ctx.$t('universal.search'),
                  "no-results-text": _ctx.$t('universal.no_items_found'),
                  "filter-results": "",
                  size: "lg",
                  "is-object": true,
                  "prop-to-check": "name",
                  class: "group mt-5 w-full outline-0",
                  "data-ignore-theme": "",
                  "append-to-document": "",
                  label: "label",
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (newSelectedCondition.value = $event))
                }, {
                  trigger: _withCtx(({ active }) => [
                    (newSelectedCondition.value?.valueRequired)
                      ? (_openBlock(), _createBlock(_unref(UiMenuSimpleTrigger), {
                          key: 0,
                          class: "[&>svg]:ml-1"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(newSelectedCondition.value?.label || _ctx.$t('conversations.filters.form.condition_placeholder')), 1 /* TEXT */)
                          ]),
                          _: 1 /* STABLE */
                        }))
                      : (_openBlock(), _createBlock(_unref(UiMenuSelectTrigger), {
                          key: 1,
                          small: "",
                          active: active
                        }, {
                          default: _withCtx(() => [
                            (newSelectedCondition.value?.label)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _createTextVNode(_toDisplayString(newSelectedCondition.value.label), 1 /* TEXT */)
                                ], 64 /* STABLE_FRAGMENT */))
                              : (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(_ctx.$t('conversations.filters.form.condition_placeholder')), 1 /* TEXT */))
                          ]),
                          _: 2 /* DYNAMIC */
                        }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["active"]))
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["mode", "model-value", "options", "search-placeholder", "no-results-text"]))
              : (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(newSelectedCondition.value?.label || _ctx.$t('conversations.filters.form.condition_placeholder')), 1 /* TEXT */))
          ]),
          selectedOption: _withCtx(() => [
            (newSelectedCondition.value?.valueRequired)
              ? (_openBlock(), _createBlock(_unref(UiFormField), {
                  key: 0,
                  class: "mt-2 flex flex-col",
                  error: 
              applyingDisabled.value && applyClicked.value
                ? _ctx.$t('filter.add', { filterName: props.filterLabel.toLocaleLowerCase() })
                : undefined
            
                }, {
                  default: _withCtx(() => [
                    (_ctx.valueType === _unref(FilterValueType).Loading)
                      ? (_openBlock(), _createBlock(_unref(UiInput), {
                          key: 0,
                          disabled: "",
                          small: "",
                          "model-value": _ctx.$t('conversations.filters.form.value_loading')
                        }, null, 8 /* PROPS */, ["model-value"]))
                      : (_ctx.valueType === _unref(FilterValueType).NoValues)
                        ? (_openBlock(), _createBlock(_unref(UiInput), {
                            key: 1,
                            disabled: "",
                            small: "",
                            "model-value": _ctx.$t('conversations.filters.form.value_empty')
                          }, null, 8 /* PROPS */, ["model-value"]))
                        : (_ctx.valueType === _unref(FilterValueType).SearchableTags)
                          ? (_openBlock(), _createBlock(TagPicker, {
                              key: 2,
                              "model-value": newValue.value as DropdownSelectedValue[],
                              "data-test": "tag-search",
                              "api-method": props.valueComponentProps?.apiMethod,
                              class: "bg-gray-10",
                              "open-direction": "auto",
                              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (newValue.value = $event))
                            }, null, 8 /* PROPS */, ["model-value", "api-method"]))
                          : (_ctx.valueType === _unref(FilterValueType).CustomField)
                            ? (_openBlock(), _createBlock(CustomFieldValueDropdown, {
                                key: 3,
                                "field-external-id": _ctx.filterId,
                                "field-name": _ctx.filterLabel,
                                "model-value": newValue.value as DropdownSelectedValue[],
                                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (newValue.value = Array.isArray($event) ? $event : [$event]))
                              }, null, 8 /* PROPS */, ["field-external-id", "field-name", "model-value"]))
                            : _createCommentVNode("v-if", true),
                    (_ctx.valueType === _unref(FilterValueType).Generic)
                      ? (_openBlock(), _createBlock(_unref(UiDropdown), {
                          key: 4,
                          mode: multipleValuesAllowed.value ? _unref(Modes).Multiple : _unref(Modes).Single,
                          groups: props.valueComponentProps.groups,
                          "model-value": newValue.value as DropdownSelectedValue[],
                          label: props.valueComponentProps.labelKey,
                          "close-on-select": !multipleValuesAllowed.value,
                          "max-width": 999,
                          size: "lg",
                          "select-all-mode": hasMultipleGroups.value ? _unref(SelectAllModes).None : _unref(SelectAllModes).All,
                          "select-all-label": !hasMultipleGroups.value ? _ctx.$t('filter.select_all') : undefined,
                          "allow-single-deselect": "",
                          "search-placeholder": _ctx.$t('universal.search'),
                          "no-results-text": props.valueComponentProps.noResultsText,
                          "is-object": true,
                          "prop-to-check": "value",
                          "has-search": "",
                          "filter-results": "",
                          class: "group outline-0",
                          "append-to-document": "",
                          disabled: props.disabled,
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (newValue.value = Array.isArray($event) ? $event : [$event]))
                        }, {
                          "option-icon": _withCtx(({ option }) => [
                            (option.type === _unref(GROUP))
                              ? (_openBlock(), _createBlock(_unref(UsersIcon), {
                                  key: 0,
                                  class: "mr-1 h-4 w-4 text-gray-50"
                                }))
                              : _createCommentVNode("v-if", true)
                          ]),
                          option: _withCtx(({ option }) => [
                            (option?.value === 'separator')
                              ? (_openBlock(), _createElementBlock("div", _hoisted_7))
                              : ('emojiSrc' in option && option?.emojiSrc)
                                ? (_openBlock(), _createElementBlock("img", {
                                    key: 1,
                                    src: option?.emojiSrc as string,
                                    class: "h-4 w-4"
                                  }, null, 8 /* PROPS */, _hoisted_8))
                                : (option?.label)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(option.label), 1 /* TEXT */))
                                  : _createCommentVNode("v-if", true)
                          ]),
                          trigger: _withCtx(({ active }) => [
                            _createVNode(_unref(UiMenuSelectTrigger), {
                              small: "",
                              active: active
                            }, {
                              default: _withCtx(() => [
                                (getEmojiSrc(newValue.value as DropdownSelectedValue[]))
                                  ? (_openBlock(), _createElementBlock("img", {
                                      key: 0,
                                      src: getEmojiSrc(newValue.value as DropdownSelectedValue[]),
                                      class: "h-4 w-4"
                                    }, null, 8 /* PROPS */, _hoisted_10))
                                  : (getFieldLabel(newValue.value as DropdownSelectedValue[]))
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                        _createTextVNode(_toDisplayString(getFieldLabel(newValue.value as DropdownSelectedValue[])), 1 /* TEXT */)
                                      ], 64 /* STABLE_FRAGMENT */))
                                    : (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(multipleValuesAllowed.value
                        ? _ctx.$t('filter.multiple_value_placeholder')
                        : _ctx.$t('filter.single_value_placeholder')), 1 /* TEXT */))
                              ]),
                              _: 2 /* DYNAMIC */
                            }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["active"])
                          ]),
                          _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["mode", "groups", "model-value", "label", "close-on-select", "select-all-mode", "select-all-label", "search-placeholder", "no-results-text", "disabled"]))
                      : _createCommentVNode("v-if", true),
                    (_ctx.valueType === _unref(FilterValueType).Date)
                      ? (_openBlock(), _createBlock(DayPicker, {
                          key: 5,
                          small: "",
                          "model-value": Array.isArray(newValue.value) ? undefined : (newValue.value as Date),
                          "initial-page-position": 1,
                          "max-date": new Date(),
                          "min-date": new Date('2018-01-01'),
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (newValue.value = $event))
                        }, null, 8 /* PROPS */, ["model-value", "max-date", "min-date"]))
                      : (_ctx.valueType === _unref(FilterValueType).Text)
                        ? (_openBlock(), _createBlock(_unref(UiInput), {
                            key: 6,
                            "model-value": newValue.value as string,
                            type: "text",
                            small: "",
                            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => (newValue.value = $event))
                          }, null, 8 /* PROPS */, ["model-value"]))
                        : (_ctx.valueType === _unref(FilterValueType).MultirowText)
                          ? (_openBlock(), _createBlock(MultirowTextInput, {
                              key: 7,
                              "model-value": newValue.value as string[],
                              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => (newValue.value = $event))
                            }, null, 8 /* PROPS */, ["model-value"]))
                          : (_ctx.valueType === _unref(FilterValueType).Integer)
                            ? (_openBlock(), _createBlock(_unref(UiInput), {
                                key: 8,
                                modelValue: newValue.value as string,
                                "onUpdate:modelValue": [
                                  _cache[9] || (_cache[9] = ($event: any) => ((newValue.value as string) = $event)),
                                  _cache[10] || (_cache[10] = (value) => validateNumber(Number(value)))
                                ],
                                type: "number",
                                min: "0",
                                small: ""
                              }, null, 8 /* PROPS */, ["modelValue"]))
                            : _createCommentVNode("v-if", true)
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["error"]))
              : _createCommentVNode("v-if", true)
          ]),
          _: 1 /* STABLE */
        }),
        _createVNode(_unref(UiButton), {
          small: "",
          "data-ignore-theme": "",
          class: "m-t-auto mt-4 w-full",
          onClick: applyFilter
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('filter.apply')), 1 /* TEXT */)
          ]),
          _: 1 /* STABLE */
        })
      ], 8 /* PROPS */, _hoisted_1)
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["trigger"]))
}
}

})